import { useGetServerTime } from '@engoo/react-server-time';
import { useCallStateContext } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { defaultLessonUIState, ILessonUIState, LessonPhase } from 'app/models/ui/call/lesson/constants';
import { getLessonPhase, getLessonUIState, notifyLessonPhaseChange } from 'app/models/ui/call/lesson/util';
import { useEffect, useMemo, useReducer } from 'react';

const useLessonUIState = (): ILessonUIState => {
  const bookingState = useBookingState();
  const { lockTime, stage } = useCallStateContext();

  const lessonState = bookingState?.bookingType === BookingType.Lesson ? bookingState : undefined;
  const lessonRole = lessonState?.lessonRole;
  const { start, end } =
    lessonState
      ? { start: new Date(lessonState.time.start), end: new Date(lessonState.time.end) }
      : { start: undefined, end: undefined };

  // Force a rerender whenever a lesson phase changes.
  const getServerTime = useGetServerTime();
  const [, forceRender] = useReducer(() => Symbol(), Symbol());

  useEffect(() => {
    if (start && end) {
      return notifyLessonPhaseChange(getServerTime(), forceRender, start, end, lockTime);
    }
  }, [end, getServerTime, lockTime, start]);

  const lessonPhase = useMemo(() => {
    return start && end ? getLessonPhase(getServerTime(), start, end, lockTime) : LessonPhase.None;
  }, [end, getServerTime, lockTime, start]);

  return useMemo(() => {
    return lessonRole ? getLessonUIState(lessonPhase, stage, lessonRole) : defaultLessonUIState;
  }, [lessonPhase, lessonRole, stage]);
};

export default useLessonUIState;
